import React, {useEffect, useContext} from 'react'
import Text from "@amzn/meridian/text"
import Heading from "@amzn/meridian/heading"
import Row from "@amzn/meridian/row"
import Table, {
    TableRow,
    TableCell,
    TableSectionHeader,
} from "@amzn/meridian/table"
import Tooltip from "@amzn/meridian/tooltip"
import {ReceiveFilterContext} from "./context/ReceiveFilterContext";
import {ReceiveOverViewContext} from "./context/ReceiveOverViewContext";
import {FormattedMessage, injectIntl} from "react-intl";
import {getRefereshInterval, parseCapabilitiesResponse} from "../util/Util";
import {ConfigContext} from "../global/context/ConfigContext";
import {CAPABILITY} from "../util/Constants";
import {getNodeCapabilityFromParent} from "../handler/ParentDataHandler";

const ReceiveOverView = ({intl}) => {
    const { cycles } = useContext(ReceiveFilterContext)
    const {viewModel, dispatch} = useContext(ReceiveOverViewContext)
    const {configDispatch, configViewModel} = useContext(ConfigContext);

    const getData = () => {
        getNodeCapabilityFromParent().then(capabilityResponse => {
            const capability = parseCapabilitiesResponse(capabilityResponse)
            configDispatch({type: 'GET_CAPABILITIES', capability});
            if(cycles.allCycles.length > 0) {
                dispatch({ type: 'LOAD_OVERVIEW', data:{filter: cycles.selectedCycles, cyclesDetail: cycles.allCycles,
                        capability: capability, dispatcher: dispatch}});
            }
        })
    }

    useEffect(() => {
        getData()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getData()
            }
        }, getRefereshInterval());
        return () => clearInterval(interval);
    }, [cycles]);

    const renderSidelinedRow = () => {
        return CAPABILITY.AR_NEXT_GEN !== configViewModel.capability && (
            <TableRow highlightOnHover={false}>
                <TableCell alignmentHorizontal="left">
                    <Text type="h100">
                        <Tooltip position="right" title={intl.formatMessage({id: "tooltip.sidelined", defaultMessage: "Shipments that are currently in the package state Held (Sidelined) and have been sidelined on induct during this cycle"})}>
                            <FormattedMessage id="receive.sidelined" defaultMessage="Sidelined"/>
                        </Tooltip>
                    </Text>
                </TableCell>
                <TableCell alignmentHorizontal="right">{viewModel.actual_sidelined}</TableCell>
            </TableRow>
        )
    }

    const renderProblemSolveRow = () => {
        return CAPABILITY.AR_NEXT_GEN === configViewModel.capability && (
            <TableRow highlightOnHover={false}>
                <TableCell alignmentHorizontal="left">
                    <Text type="h100">
                        <FormattedMessage id="receive.problem_solve" defaultMessage="Problem Solve"/>
                    </Text>
                </TableCell>
                <TableCell alignmentHorizontal="right">{viewModel.problem_solve}</TableCell>
            </TableRow>
        )
    }

    return (
        <div>
            <Row width="100%" widths={["grid-5"]} spacingInset="large">
                <div>
                    <Heading level={2} type="h400" alignment="left">
                        <FormattedMessage id="receive.overall_status" defaultMessage="Overall Status"/>
                    </Heading>
                    <Table showDividers={false} spacing="small">
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left"></TableCell>
                            <TableCell alignmentHorizontal="right">
                                <Text type="h100">
                                    <FormattedMessage id="receive.packages" defaultMessage="Packages"/>
                                </Text>
                            </TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.predicated_lmcp", defaultMessage: "LMCP forecast volume"})}>
                                        <FormattedMessage id="receive.predicted_dispatch" defaultMessage="Predicted Dispatch (LMCP)"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.total_predicated_plan}</TableCell>
                        </TableRow>
                        <TableSectionHeader>
                            <Text type="b300">
                                <FormattedMessage id="receive.container_planned_volume" defaultMessage="Container planned volume"/>
                            </Text>
                        </TableSectionHeader>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.current_cycle_planned", defaultMessage: "Sort planner (RTW) Manifested shipments"})}>
                                        <FormattedMessage id="receive.current_cycle_planned" defaultMessage="Current Cycle Planned"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.planned_cycle_volume}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.current_cycle_arrived", defaultMessage: "Planned shipments which have been arrived OR inducted OR pending re-induct"})}>
                                        <FormattedMessage id="receive.current_cycle_actual" defaultMessage="Current Cycle Arrived Actual"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_cycle_volume}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.other_unplanned_cycle_arrived_actual", defaultMessage: "Other shipments which have arrived the station"})}>
                                        <FormattedMessage id="receive.other_cycles" defaultMessage="Other/Unplanned Cycle Arrived Actual"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.other_cycle_volume}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.total_arrived_actual", defaultMessage: "Current Cycle Arrived plus Other/Unplanned Cycle Arrived Actual"})}>
                                        <FormattedMessage id="receive.total" defaultMessage="Total Arrived Actual"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">
                                {viewModel.actual_total_volume}
                            </TableCell>
                        </TableRow>
                        <TableSectionHeader>
                            <Text type="b300">
                                <FormattedMessage id="receive.current_status" defaultMessage="Current status"/>
                            </Text>
                        </TableSectionHeader>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.manifested", defaultMessage: "Shipments planned for this cycle which have been manifested to a truck that is planned for the delivery station"})}>
                                        <FormattedMessage id="receive.manifested" defaultMessage="Manifested"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_pending_depart_from_upstream}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.unplanned_manifested", defaultMessage: "Shipments NOT planned for this cycle which have been manifested to a truck that is planned for the delivery station"})}>
                                        <FormattedMessage id="receive.manifested.unplanned" defaultMessage="Unplanned Manifested"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_pending_depart_from_upstream_unplanned}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.in_transit", defaultMessage: "Shipments planned for this cycle which are in a truck in-transit to the delivery station"})}>
                                        <FormattedMessage id="receive.in_transit" defaultMessage="In Transit"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_in_transit_from_upstream}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.unplanned_in_transit", defaultMessage: "Shipments NOT planned for this cycle which are in a truck in-transit to the delivery station"})}>
                                        <FormattedMessage id="receive.in_transit.unplanned" defaultMessage="Unplanned In Transit"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_in_transit_from_upstream_unplanned}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.arrived", defaultMessage: "Shipments planned for this cycle which have arrived the Delivery station"})}>
                                        <FormattedMessage id="receive.arrived" defaultMessage="Arrived"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_pending_induct}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.unplanned_arrived", defaultMessage: "Shipments NOT planned for this cycle which have arrived the Delivery station"})}>
                                        <FormattedMessage id="receive.arrived.unplanned" defaultMessage="Unplanned Arrived"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_pending_induct_unplanned}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.marked_for_reprocess", defaultMessage: "Shipments planned for this cycle which have been resolved via Fixit"})}>
                                        <FormattedMessage id="receive.marked_for_reprocess" defaultMessage="Marked for Reprocess"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell
                                alignmentHorizontal="right">{viewModel.actual_pending_re_induct}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.unplanned_marked_for_reprocess", defaultMessage: "Shipments NOT planned for this cycle which have been resolved via Fixit"})}>
                                        <FormattedMessage id="receive.marked_for_reprocess.unplanned" defaultMessage="Unplanned Marked for Reprocess"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell
                                alignmentHorizontal="right">{viewModel.actual_pending_re_induct_unplanned}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.inducted", defaultMessage: "Shipments that are currently in this package state and have been successfully inducted during this cycle"})}>
                                        <FormattedMessage id="receive.inducted" defaultMessage="Inducted"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_inducted}</TableCell>
                        </TableRow>
                        <TableRow highlightOnHover={false}>
                            <TableCell alignmentHorizontal="left">
                                <Text type="h100">
                                    <Tooltip position="right" title={intl.formatMessage({id: "tooltip.stowed", defaultMessage: "Shipments that are currently in this package state and have been successfully stowed during this cycle"})}>
                                        <FormattedMessage id="receive.stowed" defaultMessage="Stowed"/>
                                    </Tooltip>
                                </Text>
                            </TableCell>
                            <TableCell alignmentHorizontal="right">{viewModel.actual_stowed}</TableCell>
                        </TableRow>
                        {renderSidelinedRow()}
                        {renderProblemSolveRow()}
                    </Table>
                </div>
            </Row>
        </div>
    )
}

export default injectIntl(ReceiveOverView)


// WEBPACK FOOTER //
// ./src/components/receive/ReceiveOverView.js